import { useEffect, useRef } from 'react';
import { cx } from '../cx';

export type VoiceWaveState = 'idle' | 'connecting' | 'quiet' | 'user' | 'machine';

/**
 * Onda de voz: barras que reagem ao som (frequências de um AnalyserNode), para a pessoa olhar o som e não o texto.
 * `getLevels` devolve o espectro do que está soando agora (microfone ou voz da Machine), ou null no silêncio.
 * Amarelo = a pessoa; branco = a Machine; no silêncio, respira devagar.
 */
export function VoiceWave({ state, getLevels, bars = 28, height = 160, className }: { state: VoiceWaveState; getLevels: () => Uint8Array | null; bars?: number; height?: number; className?: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    const smooth = new Float32Array(bars);
    const t0 = performance.now();
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) { canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const s = stateRef.current;
      const levels = s === 'user' || s === 'machine' ? getLevels() : null;
      const t = (performance.now() - t0) / 1000;
      const gap = 6;
      const bw = Math.max(3, (w - gap * (bars - 1)) / bars);
      const color = s === 'user' ? 'rgba(231, 221, 39, 0.95)' : s === 'machine' ? 'rgba(245, 243, 232, 0.92)' : s === 'connecting' ? 'rgba(245, 243, 232, 0.35)' : 'rgba(245, 243, 232, 0.22)';
      for (let i = 0; i < bars; i++) {
        let target: number;
        if (levels && levels.length) {
          // concentra nas frequências da voz (bins baixos), espelhado do centro para fora
          const mid = (bars - 1) / 2;
          const d = Math.abs(i - mid) / mid; // 0 no centro, 1 nas pontas
          const bin = Math.min(levels.length - 1, Math.floor(2 + d * Math.min(60, levels.length - 3)));
          target = Math.pow(levels[bin]! / 255, 1.4) * (1 - d * 0.45);
        } else {
          const breathe = s === 'connecting' ? 0.5 + 0.5 * Math.sin(t * 6 + i * 0.5) : 0.5 + 0.5 * Math.sin(t * 1.4 + i * 0.35);
          target = 0.06 + 0.08 * breathe;
        }
        smooth[i] = smooth[i]! + (target - smooth[i]!) * (target > smooth[i]! ? 0.45 : 0.18);
        const bh = Math.max(4, smooth[i]! * h);
        const x = i * (bw + gap);
        const y = (h - bh) / 2;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(x, y, bw, bh, bw / 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [bars, getLevels]);
  return <canvas ref={ref} className={cx('rr-wave', `rr-wave--${state}`, className)} style={{ height }} aria-hidden />;
}
